import { useState, useEffect, useMemo, useRef, useId } from 'react'
import { bancosApi, TIPOVALOR_LABEL } from '../api/bancos'
import { colors, font, text, space, radius, shadow, sx } from '../design/tokens'

/**
 * Selector del N_BCO real de Presea, filtrado por TIPOVALOR. Se usa en el
 * modal de recibo cuando el operador elige tarjeta/cheque/etc. — el guard
 * de ReciboPreseaService rebota cualquier código que no exista en BANCOS,
 * así que esta es la única fuente válida.
 *
 * BUSCADOR (Martin, 27 ago 2026): el maestro de cheques son 276 bancos
 * emisores; un <select> obliga a scrollear a ciegas. Ahora es un combobox:
 * se tipea y filtra, y **se busca y se muestra también por código** — el
 * operador que se sabe el N_BCO lo escribe y listo.
 *
 * No es un <datalist> (regla Martin 19 jun: mete interlineado y no deja
 * mostrar dos columnas); es una lista propia, compacta, con teclado
 * (↑/↓/Enter/Esc) y el código en mono a la izquierda.
 *
 * Props:
 *   tipo (number)    — TIPOVALOR a listar (1/4/7/8). Required.
 *   value (number)   — n_bco actualmente seleccionado.
 *   onChange (fn)    — recibe { n_bco, nombre } | null.
 *   disabled (bool)
 *   placeholder (str)
 *   required (bool)
 */
// Nombre limpio para mostrar: saca los prefijos "TC " (Tarjeta de Crédito)
// y "TD " (Tarjeta de Débito) del inicio. El contexto (medio = tarjeta) ya
// lo da el campo anterior. Los demás tipos no usan estos prefijos.
const limpiarPrefijo = (nombre) => (nombre || '').replace(/^(TC|TD)\s+/i, '')

// Sin tildes y en minúscula: "MUNICIPAL DE ROSARIO" matchea "rosario".
const norm = (s) =>
  String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase()

const etiqueta = (b) => `${b.n_bco} — ${limpiarPrefijo(b.nombre)}`

export default function SelectorBancoPresea({ tipo, value, onChange, disabled, placeholder, required }) {
  const [opciones, setOpciones] = useState([])
  const [error, setError] = useState('')
  const [cargando, setCargando] = useState(false)
  const [texto, setTexto] = useState('')      // lo tipeado mientras se busca
  const [abierto, setAbierto] = useState(false)
  const [activo, setActivo] = useState(0)     // índice resaltado por teclado
  const cajaRef = useRef(null)
  const listaRef = useRef(null)
  const listboxId = useId()

  useEffect(() => {
    if (!tipo) { setOpciones([]); return }
    setCargando(true); setError('')
    bancosApi.list({ tipo, limit: 500 })
      .then(({ data }) => setOpciones(data || []))
      .catch(e => setError(e.response?.data?.message || 'No se pudo cargar el maestro de Presea.'))
      .finally(() => setCargando(false))
  }, [tipo])

  const elegido = useMemo(
    () => opciones.find(b => String(b.n_bco) === String(value)) || null,
    [opciones, value],
  )

  // Cerrado: se ve "código — nombre". Abierto: lo que el operador va tipeando.
  const display = abierto ? texto : (elegido ? etiqueta(elegido) : '')

  // Filtra por NOMBRE o por CÓDIGO. Los match de código van primero: si tipeás
  // "29" querés el banco 29 arriba, no los diez que tienen "29" en el nombre.
  const filtradas = useMemo(() => {
    const q = norm(texto)
    if (!abierto || !q) return opciones
    const porCodigo = []
    const porNombre = []
    for (const b of opciones) {
      const cod = String(b.n_bco)
      if (cod === q || cod.startsWith(q)) porCodigo.push(b)
      else if (norm(limpiarPrefijo(b.nombre)).includes(q)) porNombre.push(b)
    }
    return [...porCodigo, ...porNombre]
  }, [opciones, texto, abierto])

  // Cerrar al clickear afuera.
  useEffect(() => {
    if (!abierto) return
    const fuera = (e) => { if (cajaRef.current && !cajaRef.current.contains(e.target)) cerrar() }
    document.addEventListener('mousedown', fuera)
    return () => document.removeEventListener('mousedown', fuera)
  }, [abierto])

  // Mantener a la vista la opción resaltada con el teclado.
  useEffect(() => {
    if (!abierto || !listaRef.current) return
    listaRef.current.querySelector('[data-activo="1"]')?.scrollIntoView({ block: 'nearest' })
  }, [activo, abierto])

  const abrir = () => { if (!disabled && !cargando) { setTexto(''); setActivo(0); setAbierto(true) } }
  const cerrar = () => { setAbierto(false); setTexto('') }

  const elegir = (b) => {
    onChange?.(b ? { n_bco: b.n_bco, nombre: b.nombre } : null)
    cerrar()
  }

  const onKeyDown = (e) => {
    if (!abierto) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') { e.preventDefault(); abrir() }
      return
    }
    if (e.key === 'ArrowDown')      { e.preventDefault(); setActivo(i => Math.min(i + 1, filtradas.length - 1)) }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActivo(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter')     { e.preventDefault(); if (filtradas[activo]) elegir(filtradas[activo]) }
    else if (e.key === 'Escape')    { e.preventDefault(); cerrar() }
    else if (e.key === 'Tab')       { cerrar() }
  }

  return (
    <div ref={cajaRef} style={st.caja}>
      <div style={st.campo}>
        <input
          type="text"
          role="combobox"
          aria-expanded={abierto}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={display}
          onChange={e => { setTexto(e.target.value); setActivo(0); if (!abierto) setAbierto(true) }}
          onFocus={abrir}
          onKeyDown={onKeyDown}
          disabled={disabled || cargando}
          // `required` va sobre el input visible: si no hay banco elegido, el
          // display queda vacío y el navegador frena el submit acá mismo.
          required={required && !elegido}
          placeholder={cargando ? 'Cargando…' : (placeholder || `Buscá ${TIPOVALOR_LABEL[tipo]?.toLowerCase() || 'una opción'} por nombre o código…`)}
          style={{ ...st.input, ...(elegido && !abierto ? st.inputElegido : null) }}
          autoComplete="off"
        />
        {elegido && !disabled && (
          <button type="button" onClick={() => elegir(null)} style={st.limpiar} title="Quitar" aria-label="Quitar">✕</button>
        )}
      </div>

      {abierto && (
        <div ref={listaRef} id={listboxId} role="listbox" style={st.lista}>
          {filtradas.length === 0 && (
            <div style={st.vacio}>Sin resultados para “{texto}”.</div>
          )}
          {filtradas.map((b, i) => (
            <div
              key={b.n_bco}
              role="option"
              aria-selected={String(b.n_bco) === String(value)}
              data-activo={i === activo ? '1' : '0'}
              onMouseEnter={() => setActivo(i)}
              onMouseDown={e => e.preventDefault()}  // no perder el foco antes del click
              onClick={() => elegir(b)}
              style={{ ...st.opcion, ...(i === activo ? st.opcionActiva : null) }}
            >
              <span style={st.codigo}>{b.n_bco}</span>
              <span style={st.nombre}>{limpiarPrefijo(b.nombre)}</span>
            </div>
          ))}
        </div>
      )}

      {error && <div style={st.error}>{error}</div>}
    </div>
  )
}

const st = {
  caja: { position: 'relative' },
  campo: { position: 'relative' },
  input: {
    width: '100%', padding: '0.5rem 1.75rem 0.5rem 0.75rem',
    border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm,
    fontSize: text.body, fontFamily: font.sans, boxSizing: 'border-box',
    background: colors.bg, color: colors.text,
  },
  // Cuando hay uno elegido se muestra "código — nombre": el código es un
  // identificador, va en mono con tabular-nums (regla tipográfica del sistema).
  inputElegido: { ...sx.mono },
  limpiar: {
    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: colors.textMeta, fontSize: text.sec, lineHeight: 1, padding: 4,
  },
  lista: {
    position: 'absolute', zIndex: 30, top: 'calc(100% + 2px)', left: 0, right: 0,
    maxHeight: 260, overflowY: 'auto', background: colors.bg,
    border: `1px solid ${colors.border}`, borderRadius: radius.sm,
    boxShadow: shadow.dropdown,
  },
  opcion: {
    display: 'flex', alignItems: 'baseline', gap: space.md,
    padding: `${space.sm}px ${space.lg}px`, cursor: 'pointer',
    fontSize: text.body, lineHeight: 1.3,
  },
  opcionActiva: { background: colors.bgSoft },
  codigo: { ...sx.mono, fontSize: text.sec, color: colors.textSec, minWidth: 38, textAlign: 'right', flexShrink: 0 },
  nombre: { color: colors.text },
  vacio: { padding: `${space.md}px ${space.lg}px`, fontSize: text.sec, color: colors.textMeta },
  error: { color: colors.err, fontSize: text.sec, marginTop: space.xs },
}
